import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { PhotoSide } from './PhotoSide';
import { FLOW_STEPS } from './flowSteps';
import { asDraft } from '../../types/registration';
import type { RegistrationDraft } from '../../types/registration';
interface ImmersiveFlowProps {
  data: Record<string, unknown>;
  onChange: (patch: Record<string, unknown>) => void;
  stepIndex: number;
  onStepChange: (index: number) => void;
  onSubmit: (draft: RegistrationDraft) => void | Promise<void>;
}
export function ImmersiveFlow({
  data,
  onChange,
  stepIndex,
  onStepChange,
  onSubmit
}: ImmersiveFlowProps) {
  const [submitting, setSubmitting] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const step = FLOW_STEPS[stepIndex];
  const total = FLOW_STEPS.length;
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === total - 1;
  const StepForm = step.Form;
  const {
    image,
    caption
  } = step.photo(data);
  useEffect(() => {
    headingRef.current?.focus();
  }, [stepIndex]);
  async function advance() {
    if (!isLast) {
      onStepChange(stepIndex + 1);
      return;
    }
    setSubmitting(true);
    await onSubmit(asDraft(data));
    setSubmitting(false);
  }
  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const target = event.target as HTMLElement;
    if (event.key !== 'Enter' || event.shiftKey) return;
    if (target.tagName === 'TEXTAREA' || target.tagName === 'BUTTON') return;
    event.preventDefault();
    void advance();
  }
  return <div className="flex min-h-screen w-full flex-col bg-canvas lg:grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1fr)]" data-id="element-395" data-mp="3trxdwjl6nqzw">
      <div className="h-[34vh] min-h-52 w-full lg:sticky lg:top-0 lg:h-screen" data-id="element-396" data-mp="q4x1cqehxjtv">
        <PhotoSide image={image} caption={caption} data-id="element-397" data-mp="q703u9wu55fh" />
      </div>

      <div className="flex flex-1 flex-col px-6 py-8 sm:px-10 lg:px-16 lg:py-10" data-id="element-398" data-mp="ldf0s3gdxl9f">
        <div className="flex items-center gap-3 pr-28 sm:pr-0" data-id="element-399" data-mp="2flsngq6mo7np">
          <span className="text-eyebrow-caption font-semibold uppercase text-ink-muted" data-id="element-400" data-mp="2kl5k22nawt3h">
            <span className="text-ink" data-id="element-401" data-mp="2kmygkp479wxr">{String(stepIndex + 1).padStart(2, '0')}</span>
            {' / '}
            {String(total).padStart(2, '0')} · {step.eyebrow}
          </span>
        </div>

        <div className="flex flex-1 flex-col justify-center py-10" onKeyDown={handleKeyDown} data-id="element-402" data-mp="2aszh5xvigr9i">
          <AnimatePresence mode="wait" data-id="element-403" data-mp="1wxnodw0x1av8">
            <motion.div key={step.id} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.24,
            ease: 'easeOut'
          }} className="flex w-full max-w-xl flex-col gap-7" data-id="element-404" data-mp="20xo3zjzr3s3v">
              <div className="flex flex-col gap-3" data-id="element-405" data-mp="3hcpkfeydswg8">
                <h1 ref={headingRef} tabIndex={-1} className="text-page-title font-semibold text-ink focus-visible:outline-none sm:text-display" data-id="element-406" data-mp="3mahfuiy8j0p6">
                  {step.question}
                </h1>
                {step.support ? <p className="text-body text-ink-muted" data-id="element-407" data-mp="1kcp3km19ywhk">{step.support}</p> : null}
              </div>

              <StepForm data={data} update={onChange} data-id="element-408" data-mp="1flyyl9wrgvne" />

              <div className="flex flex-col gap-4 border-t border-rule pt-6" data-id="element-409" data-mp="1fkdzr83eyqjq">
                <div className="flex flex-wrap items-center gap-3" data-id="element-410" data-mp="1tbsn1r4ctdwl">
                  <Button size="lg" onClick={advance} disabled={submitting} data-id="element-411" data-mp="1odir3eat299x">
                    {submitting ? 'Creating your account…' : isLast ? 'Join the portal' : 'Continue'}
                    {!submitting && !isLast ? <ArrowRightIcon aria-hidden="true" className="h-4 w-4" data-id="element-412" data-mp="8y21equu9ybo" /> : null}
                  </Button>

                  {!isLast ? <button type="button" onClick={advance} className="inline-flex min-h-tap items-center rounded-control px-1 text-small text-ink-muted underline-offset-4 hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-413" data-mp="2r7y0af9ii9o7">
                      Skip this
                    </button> : null}
                </div>

                {!isFirst ? <button type="button" onClick={() => onStepChange(stepIndex - 1)} className="inline-flex min-h-tap w-fit items-center gap-2 rounded-control text-small text-ink-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-414" data-mp="2dfztib39wpv7">
                    <ArrowLeftIcon aria-hidden="true" className="h-4 w-4" data-id="element-415" data-mp="1tq7mmevq2gw1" />
                    Back
                  </button> : null}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <p className="text-small text-ink-muted" data-id="element-416" data-mp="1h7c1yhqy7c0t">
          Nothing here is required, and closing this keeps everything you&apos;ve entered.
        </p>
      </div>
    </div>;
}